/**
 * Lucy Analytics Module — Conversation & Revenue Tracking
 * WowStore.live — Lucy Intelligence v5
 *
 * Tracks Lucy's contribution to revenue, engagement, and conversion.
 * Pushes events to GA4 (gtag), Meta Pixel (fbq), and local storage
 * for the future Lucy Analytics Dashboard.
 *
 * Integration:
 *   Loaded by theme.liquid after wowstore-ai-chatbot.liquid
 *   Hooks into: window.LucyAnalytics (global API)
 *
 * Events tracked:
 *   - chat_open: Lucy chat widget opened
 *   - chat_message: User sent a message
 *   - product_impression: Product shown in chat
 *   - product_click: Product clicked from chat
 *   - add_to_cart: Product added to cart from Lucy
 *   - capsule_shown: Capsule wardrobe displayed
 *   - try_on_start: Virtual try-on initiated from chat
 *   - voice_start: Voice session started
 *   - voice_end: Voice session ended
 *   - quick_action: Quick action button clicked
 */
(function() {
  'use strict';

  if (window._lucyAnalyticsInstance) return;
  window._lucyAnalyticsInstance = Date.now();

  /* ═══════════════════ Config ═══════════════════ */
  var STORAGE_KEY = 'lucy_analytics';
  var SESSION_KEY = 'lucy_session';
  var MAX_STORED_EVENTS = 500;
  var FLUSH_INTERVAL_MS = 30000; /* 30s */
  var GA4_ID = 'G-K3ZE533YJN';
  var META_PIXEL_ID = '2779019885638100';

  /* ═══════════════════ Session Management ═══════════════════ */

  function getSessionId() {
    var session = sessionStorage.getItem(SESSION_KEY);
    if (session) {
      try {
        var data = JSON.parse(session);
        if (Date.now() - data.startedAt < 30 * 60 * 1000) return data;
      } catch(e) { /* ignore */ }
    }

    var newSession = {
      id: 'ls_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8),
      startedAt: Date.now(),
      messageCount: 0,
      productsShown: 0,
      productsClicked: 0,
      addedToCart: 0,
      capsulesShown: 0,
      tryOnsStarted: 0,
      voiceSessions: 0,
      revenue: 0
    };

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
    return newSession;
  }

  function updateSession(updates) {
    var session = getSessionId();
    Object.keys(updates).forEach(function(k) {
      if (typeof updates[k] === 'number' && typeof session[k] === 'number') {
        session[k] += updates[k];
      } else {
        session[k] = updates[k];
      }
    });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  }

  /* ═══════════════════ Event Storage ═══════════════════ */

  function getStoredEvents() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch(e) { return []; }
  }

  function storeEvent(event) {
    try {
      var events = getStoredEvents();
      events.push(event);

      /* Trim old events */
      if (events.length > MAX_STORED_EVENTS) {
        events = events.slice(-MAX_STORED_EVENTS);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
    } catch(e) { /* storage full or blocked */ }
  }

  /* ═══════════════════ GA4 Integration ═══════════════════ */

  function pushToGA4(eventName, params) {
    if (typeof window.gtag !== 'function') return;

    try {
      var ga4Params = Object.assign({
        event_category: 'lucy_ai',
        session_id: getSessionId().id
      }, params);

      window.gtag('event', eventName, ga4Params);
    } catch(e) {
      console.warn('[Lucy Analytics] GA4 push failed:', e.message);
    }
  }

  /* ═══════════════════ Meta Pixel Integration ═══════════════════ */

  function pushToMeta(eventName, params) {
    if (typeof window.fbq !== 'function') return;

    try {
      /* Map Lucy events to Meta standard events where possible */
      var metaEventMap = {
        'add_to_cart': 'AddToCart',
        'product_click': 'ViewContent',
        'chat_open': 'Contact',
        'capsule_shown': 'CustomizeProduct'
      };

      var metaEvent = metaEventMap[eventName];
      if (metaEvent) {
        var metaParams = {};
        if (params.price) {
          metaParams.value = parseFloat(params.price) || 0;
          metaParams.currency = 'EUR';
        }
        if (params.product_title) {
          metaParams.content_name = params.product_title;
        }
        if (params.product_id) {
          metaParams.content_ids = [params.product_id];
          metaParams.content_type = 'product';
        }
        window.fbq('track', metaEvent, metaParams);
      } else {
        /* Custom event for non-standard */
        window.fbq('trackCustom', 'LucyAI_' + eventName, params);
      }
    } catch(e) {
      console.warn('[Lucy Analytics] Meta push failed:', e.message);
    }
  }

  /* ═══════════════════ Core Track Function ═══════════════════ */

  function track(eventName, params) {
    params = params || {};

    var session = getSessionId();

    var event = {
      event: eventName,
      timestamp: new Date().toISOString(),
      sessionId: session.id,
      url: window.location.pathname,
      params: params
    };

    /* Store locally */
    storeEvent(event);

    /* Push to GA4 */
    pushToGA4('lucy_' + eventName, params);

    /* Push to Meta Pixel */
    pushToMeta(eventName, params);

    /* Update session counters */
    var sessionUpdates = {};
    switch (eventName) {
      case 'chat_message':
        sessionUpdates.messageCount = 1;
        break;
      case 'product_impression':
        sessionUpdates.productsShown = (params.count || 1);
        break;
      case 'product_click':
        sessionUpdates.productsClicked = 1;
        break;
      case 'add_to_cart':
        sessionUpdates.addedToCart = 1;
        if (params.price) sessionUpdates.revenue = parseFloat(params.price) || 0;
        break;
      case 'capsule_shown':
        sessionUpdates.capsulesShown = 1;
        break;
      case 'try_on_start':
        sessionUpdates.tryOnsStarted = 1;
        break;
      case 'voice_start':
        sessionUpdates.voiceSessions = 1;
        break;
    }
    updateSession(sessionUpdates);

    console.log('[Lucy Analytics]', eventName, params.product_title || params.message_length || '');
  }

  /* ═══════════════════ Reporting ═══════════════════ */

  /**
   * Get a summary of Lucy's impact for the current session.
   */
  function getSessionSummary() {
    return getSessionId();
  }

  /**
   * Get all stored events (for dashboard/export).
   */
  function getEvents(options) {
    options = options || {};
    var events = getStoredEvents();

    /* Filter by event type */
    if (options.type) {
      events = events.filter(function(e) { return e.event === options.type; });
    }

    /* Filter by date range */
    if (options.since) {
      var since = new Date(options.since).getTime();
      events = events.filter(function(e) {
        return new Date(e.timestamp).getTime() >= since;
      });
    }

    /* Limit results */
    if (options.limit) {
      events = events.slice(-options.limit);
    }

    return events;
  }

  /**
   * Get aggregate stats across all stored events.
   */
  function getStats() {
    var events = getStoredEvents();
    var stats = {
      totalEvents: events.length,
      sessions: {},
      eventCounts: {},
      revenue: 0,
      productsShown: 0,
      productsClicked: 0,
      cartAdds: 0,
      capsules: 0,
      messages: 0
    };

    events.forEach(function(e) {
      /* Count by type */
      stats.eventCounts[e.event] = (stats.eventCounts[e.event] || 0) + 1;

      /* Track unique sessions */
      if (e.sessionId) stats.sessions[e.sessionId] = true;

      /* Aggregate metrics */
      switch (e.event) {
        case 'chat_message': stats.messages++; break;
        case 'product_impression': stats.productsShown += (e.params.count || 1); break;
        case 'product_click': stats.productsClicked++; break;
        case 'add_to_cart':
          stats.cartAdds++;
          stats.revenue += parseFloat(e.params.price) || 0;
          break;
        case 'capsule_shown': stats.capsules++; break;
      }
    });

    stats.uniqueSessions = Object.keys(stats.sessions).length;
    delete stats.sessions;

    /* Calculated rates */
    stats.clickRate = stats.productsShown > 0
      ? (stats.productsClicked / stats.productsShown * 100).toFixed(1) + '%'
      : '0%';
    stats.cartRate = stats.productsClicked > 0
      ? (stats.cartAdds / stats.productsClicked * 100).toFixed(1) + '%'
      : '0%';

    return stats;
  }

  /**
   * Clear all stored analytics data.
   */
  function clearData() {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    console.log('[Lucy Analytics] All data cleared');
  }

  /* ═══════════════════ Public API ═══════════════════ */

  window.LucyAnalytics = {
    track: track,
    getSession: getSessionSummary,
    getEvents: getEvents,
    getStats: getStats,
    clearData: clearData
  };

  /* Track page view on load */
  track('page_view', {
    page: window.location.pathname,
    referrer: document.referrer || 'direct'
  });

  console.log('[Lucy Analytics] Module loaded — tracking Lucy\'s impact');
})();
